import React from 'react';
import { ShieldCheck } from 'lucide-react';

const NPP = () => {
  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-6">
            <ShieldCheck className="text-[#0F4C81]" size={48} />
          </div>
          <h1 className="text-5xl font-bold text-[#12324A] mb-6 text-center">
            Notice of Privacy Practices
          </h1>
          <p className="text-lg text-[#5F6C7B] text-center">
            Effective Date: {new Date().toLocaleDateString()}
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="bg-[#F4F6F8] rounded-xl p-6 mb-8">
              <p className="text-[#5F6C7B] m-0">
                This notice describes how medical information about you may be used and disclosed and how you can get access to this information. Please review it carefully.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Your Health Information Rights</h2>
            <p className="text-[#5F6C7B] mb-4">
              You have the following rights regarding your health information:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="text-[#5F6C7B]">
                <strong>Right to Access:</strong> You have the right to inspect and obtain a copy of your health information.
              </li>
              <li className="text-[#5F6C7B]">
                <strong>Right to Amend:</strong> If you believe your health information is incorrect or incomplete, you may request an amendment.
              </li>
              <li className="text-[#5F6C7B]">
                <strong>Right to Accounting of Disclosures:</strong> You have the right to receive a list of certain disclosures we have made of your health information.
              </li>
              <li className="text-[#5F6C7B]">
                <strong>Right to Request Restrictions:</strong> You may request restrictions on certain uses and disclosures of your health information.
              </li>
              <li className="text-[#5F6C7B]">
                <strong>Right to Request Confidential Communications:</strong> You may request that we communicate with you about your health matters in a specific way or at a specific location.
              </li>
              <li className="text-[#5F6C7B]">
                <strong>Right to a Paper Copy:</strong> You have the right to a paper copy of this notice at any time.
              </li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">How We May Use and Disclose Your Health Information</h2>
            
            <h3 className="text-2xl font-semibold text-[#12324A] mt-8 mb-3">For Treatment</h3>
            <p className="text-[#5F6C7B] mb-6">
              We may use and disclose your health information to provide, coordinate, or manage your healthcare and related services. This includes coordination with other healthcare providers involved in your care.
            </p>

            <h3 className="text-2xl font-semibold text-[#12324A] mt-8 mb-3">For Payment</h3>
            <p className="text-[#5F6C7B] mb-6">
              We may use and disclose your health information to bill and collect payment for services we provide. This includes communication with your insurance company to determine coverage and obtain payment.
            </p>

            <h3 className="text-2xl font-semibold text-[#12324A] mt-8 mb-3">For Healthcare Operations</h3>
            <p className="text-[#5F6C7B] mb-6">
              We may use and disclose your health information for our business operations, including quality assessment, training, licensing, and business management activities.
            </p>

            <h3 className="text-2xl font-semibold text-[#12324A] mt-8 mb-3">As Required by Law</h3>
            <p className="text-[#5F6C7B] mb-6">
              We will disclose your health information when required to do so by federal, state, or local law.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Special Situations</h2>
            <p className="text-[#5F6C7B] mb-4">
              We may use or disclose your health information without your authorization in the following situations:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">To avert a serious threat to health or safety</li>
              <li className="text-[#5F6C7B]">For public health activities (e.g., disease reporting)</li>
              <li className="text-[#5F6C7B]">To report abuse, neglect, or domestic violence</li>
              <li className="text-[#5F6C7B]">For health oversight activities</li>
              <li className="text-[#5F6C7B]">For judicial and administrative proceedings</li>
              <li className="text-[#5F6C7B]">For law enforcement purposes</li>
              <li className="text-[#5F6C7B]">To coroners, medical examiners, and funeral directors</li>
              <li className="text-[#5F6C7B]">For research purposes (with appropriate approvals)</li>
              <li className="text-[#5F6C7B]">For workers' compensation purposes</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Your Authorization</h2>
            <p className="text-[#5F6C7B] mb-6">
              Other uses and disclosures of your health information not covered by this notice will be made only with your written authorization. You may revoke your authorization at any time by submitting a written request.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Our Responsibilities</h2>
            <p className="text-[#5F6C7B] mb-4">
              We are required by law to:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Maintain the privacy of your health information</li>
              <li className="text-[#5F6C7B]">Provide you with this notice of our legal duties and privacy practices</li>
              <li className="text-[#5F6C7B]">Follow the terms of the notice currently in effect</li>
              <li className="text-[#5F6C7B]">Notify you if we are unable to agree to a requested restriction</li>
              <li className="text-[#5F6C7B]">Notify you if a breach of your unsecured health information occurs</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Changes to This Notice</h2>
            <p className="text-[#5F6C7B] mb-6">
              We reserve the right to change this notice and to make the revised notice effective for health information we already have as well as any information we receive in the future. The current notice will be posted on our website and available upon request.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Complaints</h2>
            <p className="text-[#5F6C7B] mb-6">
              If you believe your privacy rights have been violated, you may file a complaint with TouchScreenMD or with the U.S. Department of Health and Human Services Office for Civil Rights. You will not be retaliated against for filing a complaint.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">Contact Information</h2>
            <p className="text-[#5F6C7B] mb-4">
              For questions about this notice or to exercise your rights, contact:
            </p>
            <div className="bg-[#F4F6F8] rounded-xl p-6">
              <p className="text-[#5F6C7B] m-0"><strong>Privacy Officer</strong></p>
              <p className="text-[#5F6C7B] m-0 mt-2"><strong>Email:</strong> privacy@touchscreenmd.com</p>
              <p className="text-[#5F6C7B] m-0 mt-2"><strong>Phone:</strong> 1-800-TOUCH-MD</p>
              <p className="text-[#5F6C7B] m-0 mt-2"><strong>Mail:</strong> TouchScreenMD Privacy Office, United States</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NPP;
